import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, AlertTriangle, Loader2, CheckCircle2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MaintenancePanel — Pre-launch test-data wipe (iter114s.82 UI wrapper).
 *
 * Two-stage confirmation flow:
 *   Stage 1: User clicks "Wipe Test Data" → modal opens, fires the
 *            endpoint with `dry_run: true` and renders the preview
 *            (kept users + per-collection deletion counts).
 *   Stage 2: User types "WIPE" into the confirmation input + clicks
 *            "Confirm Wipe" → fires the same endpoint without dry_run.
 *
 * Why two stages: protects against the operator accidentally nuking
 * real data after launch. The typed confirmation matches the pattern
 * GitHub uses for destructive repo actions.
 *
 * The endpoint itself (POST /api/admin/dev/wipe-test-data) is
 * admin-gated, audit-logged, and idempotent — see the route handler
 * in /app/backend/routes/admin.py for the full contract.
 */
const MaintenancePanel = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [stage, setStage] = useState('idle'); // idle | preview | confirming | wiping | done
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [result, setResult] = useState(null);

  const openModal = async () => {
    setModalOpen(true);
    setStage('preview');
    setPreview(null);
    setConfirmText('');
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/dev/wipe-test-data`,
        { confirm: 'YES_WIPE_TEST_DATA', dry_run: true },
        { withCredentials: true },
      );
      setPreview(res.data);
      setStage('confirming');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview wipe.');
      setModalOpen(false);
      setStage('idle');
    }
  };

  const closeModal = () => {
    if (stage === 'wiping') return; // can't close mid-wipe
    setModalOpen(false);
    setStage('idle');
  };

  const runWipe = async () => {
    setStage('wiping');
    try {
      const res = await axios.post(
        `${API}/api/admin/dev/wipe-test-data`,
        { confirm: 'YES_WIPE_TEST_DATA' },
        { withCredentials: true },
      );
      setResult(res.data);
      setStage('done');
      toast.success('Test data wiped. Dashboard now empty.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wipe failed.');
      setStage('confirming');
    }
  };

  const canConfirm = confirmText.trim().toUpperCase() === 'WIPE';

  return (
    <div className="bg-white rounded-lg shadow-sm border-2 border-red-200 p-4" data-testid="admin-maintenance-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-full bg-red-50 p-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-bold text-[#14281D]">Pre-Launch Maintenance</h2>
          <p className="text-xs text-[#1F3D2C]/70 mt-0.5">
            One-shot utility to clear accumulated test orders, users, quotes, artwork,
            and audit history. Keeps your admin accounts (<code>jfishburn@EG3.ca</code>,{' '}
            <code>chris@EG3.ca</code>) and <code>ppppixelperfectproduction@gmail.com</code>{' '}
            intact. Does NOT touch system secrets, pricing tables, or active sessions.
          </p>
        </div>
      </div>
      <button
        onClick={openModal}
        className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-md transition-colors"
        data-testid="admin-wipe-test-data-btn"
      >
        <Trash2 className="w-4 h-4" />
        Wipe Test Data
      </button>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="admin-wipe-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-[#14281D] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Wipe Test Data
              </h3>
              <button
                onClick={closeModal}
                disabled={stage === 'wiping'}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                data-testid="admin-wipe-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {stage === 'preview' && (
                <div className="flex items-center gap-3 text-sm text-[#1F3D2C]/70">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating preview…
                </div>
              )}

              {(stage === 'confirming' || stage === 'wiping') && preview && (
                <>
                  <div>
                    <h4 className="text-sm font-bold text-[#14281D] mb-2">
                      Users that will be KEPT ({preview.kept_users?.length || 0})
                    </h4>
                    <ul className="text-sm space-y-1 bg-green-50 border border-green-200 rounded p-3">
                      {preview.kept_users?.map((u) => (
                        <li key={u.id} className="flex items-center gap-2 text-green-800">
                          <CheckCircle2 className="w-4 h-4" />
                          <code>{u.email}</code>
                        </li>
                      ))}
                    </ul>
                    {preview.kept_users?.length < 3 && (
                      <p className="text-xs text-amber-700 mt-2">
                        ⚠️ Only {preview.kept_users.length} of 3 keeper users found.
                        Make sure all required admin accounts exist before proceeding.
                      </p>
                    )}
                  </div>

                  <div>
                    <h4 className="text-sm font-bold text-[#14281D] mb-2">Will be DELETED</h4>
                    <div className="grid grid-cols-2 gap-2 bg-red-50 border border-red-200 rounded p-3">
                      {Object.entries(preview.counts || {})
                        .filter(([k]) => k !== 'users_kept')
                        .map(([k, v]) => (
                          <div key={k} className="flex justify-between text-sm">
                            <code className="text-red-800">{k}</code>
                            <span className="font-semibold text-red-900">{v.toLocaleString()}</span>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <label className="text-sm font-semibold text-[#14281D]">
                      Type <code className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">WIPE</code> to confirm:
                    </label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      disabled={stage === 'wiping'}
                      placeholder="WIPE"
                      className="mt-2 w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:border-red-500 focus:outline-none disabled:bg-gray-50 font-mono text-sm"
                      data-testid="admin-wipe-confirm-input"
                      autoFocus
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={closeModal}
                      disabled={stage === 'wiping'}
                      className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={runWipe}
                      disabled={!canConfirm || stage === 'wiping'}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="admin-wipe-execute-btn"
                    >
                      {stage === 'wiping' && <Loader2 className="w-4 h-4 animate-spin" />}
                      {stage === 'wiping' ? 'Wiping…' : 'Confirm Wipe'}
                    </button>
                  </div>
                </>
              )}

              {stage === 'done' && result && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-6 h-6" />
                    <span className="font-bold">Wipe complete</span>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3">
                    <h4 className="text-xs font-bold text-[#14281D] uppercase tracking-wider mb-2">Records deleted</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(result.deleted || {}).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-sm">
                          <code className="text-[#1F3D2C]/70">{k}</code>
                          <span className="font-semibold">{v.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-[#1F3D2C]/70">
                    Refresh any admin tab to confirm the cockpit is empty.
                  </p>
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={() => { closeModal(); window.location.reload(); }}
                      className="px-4 py-2 bg-[#2F5D3A] hover:bg-[#1F3D2C] text-white text-sm font-semibold rounded-md"
                      data-testid="admin-wipe-done-btn"
                    >
                      Reload Dashboard
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenancePanel;
